import { Timestamp } from "firebase/firestore";
import { TimestampMaxYear } from "./types";

export const EVENT_TIME_MIN = "07:00";
export const EVENT_TIME_MAX = "01:00";
export const EVENT_TIME_DEFAULT = "19:00";

export const MONTH_STARTING_FINANCIAL_YEAR = 5; // in JS months, which is 0-indexed

const today = new Date();
export const startCalendarYear = new Date(new Date().getUTCFullYear(), 0, 1);

export const startPreviousCalendarYear = new Date(startCalendarYear.valueOf());
startPreviousCalendarYear.setFullYear(
  startPreviousCalendarYear.getFullYear() - 1
);

export const endPreviousCalendarYear = new Date(startCalendarYear.valueOf());
endPreviousCalendarYear.setDate(endPreviousCalendarYear.getDate() - 1);

export const startFinancialYear = new Date(
  new Date().getFullYear() -
    (today.getMonth() > MONTH_STARTING_FINANCIAL_YEAR ? 0 : 1),
  MONTH_STARTING_FINANCIAL_YEAR,
  1
);

export const startPreviousFinancialYear = new Date(
  startFinancialYear.valueOf()
);
startPreviousFinancialYear.setFullYear(
  startPreviousFinancialYear.getFullYear() - 1
);

export const endPreviousFinancialYear = new Date(startFinancialYear.valueOf());
endPreviousFinancialYear.setDate(endPreviousFinancialYear.getDate() - 1);

export const HOME_TIMEZONE = "Australia/Adelaide";

export const WEEKDAY_NAMES = Array.from(Array(7).keys()).map((dayIdx) => {
  const date = new Date();
  date.setDate(date.getDate() + dayIdx - date.getDay());
  return date.toLocaleDateString(undefined, { weekday: "long" });
});

export const MONTH_NAMES = Array.from(Array(12).keys()).map((monthIdx) => {
  const date = new Date();
  date.setDate(1);
  date.setMonth(monthIdx);
  return date.toLocaleDateString(undefined, { month: "long" });
});

export const timestampOrDateToDate = (
  timestamp: Timestamp | Date | { seconds: number; nanoseconds: number }
) => {
  if ("toDate" in timestamp) {
    return timestamp.toDate();
  }
  if ("seconds" in timestamp) {
    return new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000);
  }
  return timestamp;
};

export const hourToAMPM = (hour: number, minute?: number) => {
  hour = hour % 24;
  const suffix = hour < 12 ? "am" : "pm";
  hour = hour % 12;
  if (hour === 0) {
    hour = 12;
  }

  if (minute) {
    return hour + ":" + zeroPad(minute) + suffix;
  }
  return hour + suffix;
};

export const hhcmmToFractionalHour = (hhmm: string) => {
  if (hhmm.includes(":")) {
    const [hh, mm] = hhmm.split(":");
    return parseInt(hh) + parseInt(mm) / 60;
  }
  const len = hhmm.length;
  return (
    parseInt(hhmm.substring(0, len - 2)) +
    parseInt(hhmm.substring(len - 2, len)) / 60
  );
};

export const fractionalHourTohhmm = (hour: number) => {
  return zeroPad(Math.floor(hour)) + zeroPad((hour * 60) % 60);
};

export const minutesToHHMM = (minutes: number) => {
  const min = minutes % 60;
  const hour = Math.floor(minutes / 60);
  const minutePlural = min === 1 ? " minute" : " minutes";
  if (hour) {
    const hourPlural = hour === 1 ? " hour" : " hours";
    if (min) {
      return hour + hourPlural + " " + zeroPad(min) + minutePlural;
    }
    return hour + hourPlural;
  }
  return min + minutePlural;
};

export const msToMMSS = (millis: number) => {
  const asSec = Math.floor(millis / 1000);
  const mins = Math.floor(asSec / 60);
  const secs = asSec % 60;
  return mins + ":" + zeroPad(secs);
};

export const zeroPad = (num: number, len = 2) => {
  const asStr = num.toString();
  const padCount = len - asStr.length;
  if (padCount <= 0) {
    return num.toString();
  }
  return "0".repeat(padCount) + num.toString();
};

export const dateFormat = (timestamp?: Timestamp | Date) => {
  if (!timestamp) {
    return;
  }

  const date = timestampOrDateToDate(timestamp);
  const dateStr = `${date.getFullYear()}-${zeroPad(
    date.getMonth() + 1
  )}-${zeroPad(date.getDate())}`;
  return dateStr;
};

export const dateFormatAsLocal = (timestamp?: Timestamp) => {
  if (!timestamp) {
    return;
  }

  const date = timestamp.toDate();
  const dateStr = `${date.getUTCFullYear()}-${zeroPad(
    date.getUTCMonth() + 1
  )}-${zeroPad(date.getUTCDate())}`;
  return dateStr;
};

export const dateTimeLocalFormat = (timestamp?: Timestamp) => {
  if (!timestamp) {
    return;
  }
  const date = timestamp.toDate();
  const dateStr = `${date.getFullYear()}-${zeroPad(
    date.getMonth() + 1
  )}-${zeroPad(date.getDate())}`;
  const timeStr = `${zeroPad(date.getHours())}:${zeroPad(date.getMinutes())}`;
  return dateStr + "T" + timeStr;
};

export const dateFormatAU = (
  timestamp?: Timestamp | Date,
  includeDOW = false
) => {
  if (!timestamp) {
    return;
  }

  const date = timestampOrDateToDate(timestamp);

  if (date.getFullYear() >= TimestampMaxYear) {
    // Not a real date - it's a placeholder for pass expiry that means the pass isn't started yet
    return "";
  }

  const dow = includeDOW
    ? date.toLocaleString("en-AU", { weekday: "long" }) + " "
    : "";
  const dateStr = `${dow}${zeroPad(date.getDate())}/${zeroPad(
    date.getMonth() + 1
  )}/${date.getFullYear()}`;
  return dateStr;
};

export const timeBucket = (timestamp: Timestamp | Date, bucketSize = 5) => {
  const when = timestampOrDateToDate(timestamp);
  const hour = when.getHours();
  const min = Math.floor(when.getMinutes() / bucketSize) * bucketSize;
  return zeroPad(hour) + ":" + zeroPad(min);
};

export const parseAUDate = (when: string) => {
  const split = when.match(/^\s*(\d{1,2})[-/ ](\d{1,2})[-/ ](\d{2}|\d{4})\s*$/);
  if (!split) {
    return;
  }
  return new Date(
    Date.UTC(
      parseInt(split[3]),
      parseInt(split[2]) - 1,
      parseInt(split[1]),
      0,
      0,
      0,
      0
    )
  );
};

export const dateRangeFormat = (
  startTimestamp: Timestamp,
  endTimestamp?: Timestamp,
  showTime?: boolean,
  timeZone = "Australia/Adelaide"
) => {
  const start = startTimestamp.toDate();
  const end = endTimestamp?.toDate();
  const differentYear = start.getFullYear() !== new Date().getFullYear();

  const includeTime = showTime ?? start.getHours() > 0;
  const formatOptionsBaseTime = {
    hour: includeTime ? "numeric" : undefined,
    minute: includeTime ? "numeric" : undefined,
    second: undefined,
    timeZone,
  } as const;
  const formatOptionsBase = {
    ...formatOptionsBaseTime,
    year: differentYear ? "numeric" : undefined,
    month: "long",
    day: "numeric",
  } as const;
  if (!end) {
    return start.toLocaleDateString("en-AU", formatOptionsBase);
  }

  const singleDay = sameDate(start, end);
  const sameTime =
    start.getHours() === end.getHours() &&
    start.getMinutes() === end.getMinutes();
  if (singleDay) {
    if (includeTime && !sameTime) {
      return (
        // same date, so only time needed for end
        start.toLocaleDateString("en-AU", formatOptionsBase) +
        " - " +
        end.toLocaleTimeString("en-AU", formatOptionsBaseTime)
      );
    } else {
      // same day, no time shown
      return start.toLocaleDateString("en-AU", formatOptionsBase);
    }
  }

  return (
    start.toLocaleDateString("en-AU", formatOptionsBase) +
    " - " +
    end.toLocaleDateString("en-AU", formatOptionsBase)
  );
};

export const weeksAway = (millis: number) => {
  const weeks = millis / 1000 / 60 / 60 / 24 / 7;
  if (weeks < 1) {
    return "this week";
  }
  const rounded = Math.floor(weeks);
  return `in ${rounded} week${rounded !== 1 ? "s" : ""}`;
};

export const sameDate = (a: Date, b: Date) => {
  if (a.getDate() !== b.getDate()) {
    return false;
  }
  if (a.getMonth() !== b.getMonth()) {
    return false;
  }
  if (a.getFullYear() !== b.getFullYear()) {
    return false;
  }
  return true;
};

export const timeFormat = (when: Date, includeDate: boolean) => {
  const formatOptionsBase = {
    hour: "numeric",
    minute: "numeric",
    day: includeDate ? "numeric" : undefined,
    month: includeDate ? "numeric" : undefined,
    year: includeDate ? "numeric" : undefined,
  } as const;
  return when.toLocaleTimeString("en-AU", formatOptionsBase);
};

export const allTimezones = (): string[] => {
  if (Object.prototype.hasOwnProperty.call(Intl, "supportedValuesOf")) {
    try {
      const timezones = (
        Intl as unknown as { supportedValuesOf(input: string): string[] }
      ).supportedValuesOf("timeZone");
      return [
        ...timezones.filter((timezone) => timezone.startsWith("Australia")),
        ...timezones.filter((timezone) => !timezone.startsWith("Australia")),
      ];
    } catch {
      // fall through
    }
  }
  return [
    "Australia/Adelaide",
    "Australia/Brisbane",
    "Australia/Darwin",
    "Australia/Hobart",
    "Australia/Melbourne",
    "Australia/Perth",
    "Australia/Sydney",
  ];
};

export const daysInRange = (
  start: Timestamp | Date,
  end?: Timestamp | Date
) => {
  // inclusive of start & end days; ignores time (and TZ)
  if (!end) {
    return 1;
  }
  const startDate = "toDate" in start ? start.toDate() : start;
  const endDate = "toDate" in end ? end.toDate() : end;
  let days = 0;
  const i = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  while (i <= endDate) {
    ++days;
    i.setDate(i.getDate() + 1);
  }
  return days;
};

export const toLocalISOString = (
  timestamp: Timestamp | Date,
  timeZone = "Australia/Adelaide"
) => {
  const date = timestampOrDateToDate(timestamp);

  const offsetStr = date
    .toLocaleDateString(undefined, { timeZone, timeZoneName: "longOffset" })
    .slice(-6);

  const dateStr = `${date.getFullYear()}-${zeroPad(
    date.getMonth() + 1
  )}-${zeroPad(date.getDate())}`;
  const timeStr = `${zeroPad(date.getHours())}:${zeroPad(
    date.getMinutes()
  )}:${zeroPad(date.getSeconds())}`;

  return `${dateStr}T${timeStr}${offsetStr}`;
};

export const toLocalICSDateTime = (
  timestamp: Timestamp | Date,
  timeZone = "Australia/Adelaide"
) => {
  const date = timestampOrDateToDate(timestamp);

  const offsetStr = date
    .toLocaleDateString(undefined, { timeZone, timeZoneName: "longOffset" })
    .slice(-6);

  const offsetSign = offsetStr[0] === "+" ? 1 : -1;
  const offsetHour = parseInt(offsetStr.slice(1, 3));
  const offsetMin = parseInt(offsetStr.slice(4, 6));

  date.setHours(date.getHours() + offsetSign * offsetHour);
  date.setMinutes(date.getMinutes() + offsetSign * offsetMin);

  const dateStr = `${date.getUTCFullYear()}${zeroPad(
    date.getUTCMonth() + 1
  )}${zeroPad(date.getUTCDate())}`;
  const timeStr = `${zeroPad(date.getUTCHours())}${zeroPad(
    date.getUTCMinutes()
  )}${zeroPad(date.getUTCSeconds())}`;

  return `;TZID="${timeZone}":${dateStr}T${timeStr}`;
};

export const toGoogleCalendarDateString = (timestamp: Timestamp | Date) => {
  const date = timestampOrDateToDate(timestamp);

  return `${date.getUTCFullYear()}${zeroPad(date.getUTCMonth() + 1)}${zeroPad(
    date.getUTCDate()
  )}T${zeroPad(date.getUTCHours())}${zeroPad(date.getUTCMinutes())}${zeroPad(
    date.getUTCSeconds()
  )}Z`;
};

export const dayOfYear = (timestamp: Timestamp | Date) => {
  const date = timestampOrDateToDate(timestamp);
  return (
    (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
      Date.UTC(date.getFullYear(), 0, 0)) /
    24 /
    60 /
    60 /
    1000
  );
};

export const daysUntilBirthday = (timestamp: Timestamp | Date) => {
  const birthdayOfYear = dayOfYear(timestampOrDateToDate(timestamp));
  const delta = birthdayOfYear - dayOfYear(new Date());
  if (delta < -180) {
    const nextYear = new Date();
    nextYear.setUTCFullYear(nextYear.getUTCFullYear() + 1);
    return birthdayOfYear - dayOfYear(nextYear);
  }
  return delta;
};
